import { useState, useEffect, useCallback } from "react";

export const TABLE_VIEWPORT = {
  width: 1072,
  height: 792,
};

const MOBILE_MAX_WIDTH = 768;

export function useViewportFit({
  headerRef,
  tabsRef,
  phaseBannerRef,
  chatOpen = false,
  activeTab = "table",
}) {
  const [scale, setScale] = useState(1);
  const [availHeight, setAvailHeight] = useState(
    typeof window !== "undefined" ? window.innerHeight : TABLE_VIEWPORT.height
  );
  const [viewportWidth, setViewportWidth] = useState(
    typeof window !== "undefined" ? window.innerWidth : TABLE_VIEWPORT.width
  );

  const measure = useCallback(() => {
    const vw = window.innerWidth;
    setViewportWidth(vw);
    const isMobile = vw <= MOBILE_MAX_WIDTH;
    const pad = isMobile ? 6 : 10;
    const chatWidth = chatOpen && !isMobile ? 300 : 0;
    const headerH = headerRef?.current?.offsetHeight ?? 0;
    const tabsH = tabsRef?.current?.offsetHeight ?? 0;
    const phaseBannerH = phaseBannerRef?.current?.offsetHeight ?? 0;
    const availW = Math.max(vw - pad * 2 - chatWidth, 240);
    const availH = Math.max(
      window.innerHeight - headerH - tabsH - phaseBannerH - pad * 2,
      240
    );
    const fitW = availW / TABLE_VIEWPORT.width;
    const fitH = availH / TABLE_VIEWPORT.height;
    const fit = Math.min(fitW, fitH);
    const maxScale = isMobile ? 1.2 : 1.18;
    const minScale = isMobile ? 0.48 : 0.78;
    const boost = isMobile ? 1 : 1.04;
    const widthSafe = isMobile ? Math.min(fit, fitW) : fit;
    const next = widthSafe * boost;
    setScale(Math.max(minScale, Math.min(next, maxScale)));
    setAvailHeight(availH);
  }, [headerRef, tabsRef, phaseBannerRef, chatOpen, activeTab]);

  useEffect(() => {
    measure();
    window.addEventListener("resize", measure);
    const ro = typeof ResizeObserver !== "undefined" ? new ResizeObserver(measure) : null;
    [headerRef, tabsRef, phaseBannerRef].forEach((ref) => {
      if (ref?.current && ro) ro.observe(ref.current);
    });
    return () => {
      window.removeEventListener("resize", measure);
      ro?.disconnect();
    };
  }, [measure, headerRef, tabsRef, phaseBannerRef]);

  return { scale, design: TABLE_VIEWPORT, viewportWidth, availHeight };
}
